import { useRef, useEffect } from 'react';
import { Howl } from 'howler';
import { atom, useAtom, useAtomValue } from 'jotai';
import { sfxVolumeStorage } from '~/core/storages';
import { useResolution } from '../hooks/useResolution';

type SoundConfig = {
  src: string | Promise<{ default: string }>;
  volume?: number;
  autoplay?: boolean;
  loop?: boolean;
};

const SOUNDS = {
  click: {
    src: import('../sounds/click.wav'),
    volume: 0.15,
    autoplay: false,
    loop: false,
  },
  special: {
    src: import('../sounds/score.wav'),
    volume: 0.4,
    autoplay: false,
    loop: false,
  },
  error: {
    src: import('../sounds/error.wav'),
    volume: 0.3,
    autoplay: false,
    loop: false,
  },
  tick: {
    src: import('../sounds/click.wav'),
    volume: 0.3,
    autoplay: false,
    loop: false,
  },
  btClick: {
    src: import('../assets/sounds/bt-click.mp3'),
    volume: 1,
    autoplay: false,
    loop: false,
  },

  mul1: {
    src: '/blinko/SOUND/mul-1.mp3',
    volume: 0.7,
    autoplay: false,
    loop: false,
  },
  mul2: {
    src: '/blinko/SOUND/mul-2.mp3',
    volume: 0.7,
    autoplay: false,
    loop: false,
  },
  mul3: {
    src: '/blinko/SOUND/mul-3.mp3',
    volume: 0.7,
    autoplay: false,
    loop: false,
  },
  mul4: {
    src: '/blinko/SOUND/mul-4.mp3',
    volume: 0.7,
    autoplay: false,
    loop: false,
  },
  mul5: {
    src: '/blinko/SOUND/mul-5.mp3',
    volume: 0.9,
    autoplay: false,
    loop: false,
  },
  playCharge: {
    src: '/blinko/SOUND/play-charge.mp3',
    volume: 0.4,
    autoplay: false,
    loop: false,
  },
  playChargeMax: {
    src: '/blinko/SOUND/play-charge-max.mp3',
    volume: 0.4,
    autoplay: false,
    loop: true,
  },
  playChargeEnd: {
    src: '/blinko/SOUND/play-charge-end.mp3',
    volume: 1,
    autoplay: false,
    loop: false,
  },
  characterPukeSingle: {
    src: '/blinko/SOUND/character-puke-single.mp3',
    volume: 0.5,
    autoplay: false,
    loop: false,
  },
  resultCount: {
    src: '/blinko/SOUND/result-count.mp3',
    volume: 1,
    autoplay: false,
    loop: true,
  },
  resultHighlight: {
    src: '/blinko/SOUND/result-highlight.mp3',
    volume: 1,
    autoplay: false,
    loop: false,
  },
  characterPuke: {
    src: '/blinko/SOUND/character-puke.mp3',
    volume: 0.3,
    autoplay: false,
    loop: false,
  },
  honeyPickup: {
    src: '/blinko/SOUND/honey-pickup.mp3',
    volume: 0.3,
    autoplay: false,
    loop: false,
  },
  levelUp: {
    src: '/blinko/SOUND/level-up.mp3',
    volume: 1,
    autoplay: false,
    loop: false,
  },
  blinkoTransition: {
    src: '/blinko/SOUND/blinko-transition.mp3',
    volume: 1,
    autoplay: false,
    loop: false,
  },
  blinko: {
    src: '/blinko/SOUND/blinko.mp3',
    volume: 1,
    autoplay: false,
    loop: false,
  },
} as const satisfies Record<string, SoundConfig>;

export type SoundKey = keyof typeof SOUNDS;

const atomSounds = atom<Partial<Record<SoundKey, Howl>>>({});
const atomSfxVolume = atom(1);

const clampVolume = (value: number) => Math.max(0, Math.min(1, value));

const resolveSrc = async (src: SoundConfig['src']) => {
  if (typeof src === 'string') return src;
  const resolved = await src;
  return resolved.default;
};

export const useSound = () => {
  const sounds = useAtomValue(atomSounds);
  const [volume, setVolume] = useAtom(atomSfxVolume);

  const refSounds = useRef(sounds);
  refSounds.current = sounds

  const play = (name: SoundKey) => {
    const sound = refSounds.current[name];
    if (!sound) return;

    const baseVolume = SOUNDS[name]?.volume ?? 1;
    const scaledVolume = baseVolume * volume;
    sound.volume(scaledVolume);
    sound.play();
  };

  const stop = (name: SoundKey, fadeDuration?: number) => {
    const sound = refSounds.current[name];
    if (!sound) return;

    if (fadeDuration && fadeDuration > 0) {
      const currentVol = sound.volume();
      sound.fade(currentVol, 0, fadeDuration);
      sound.once('fade', () => {
        sound.stop();
        sound.off('fade');
      });
    } else {
      sound.stop();
    }
  };

  const setSfxVolume = (nextVolume: number) => {
    setVolume(clampVolume(nextVolume));
  };
  
  return {
    play,
    stop,
    sfxVolume: volume,
    setSfxVolume,
  };
}

export const SoundHandler = () => {
  const { isMobile } = useResolution();

  const [sounds, setSounds] = useAtom(atomSounds);
  const [volume, setVolume] = useAtom(atomSfxVolume);

  const refLoaded = useRef(false);
  const refSounds = useRef(sounds);
  const refVolume = useRef(volume);

  useEffect(() => {
    refVolume.current = volume;
  }, [volume]);

  useEffect(() => {
    if (!refLoaded.current) return;

    sfxVolumeStorage.set(volume.toString());
    Object.entries(refSounds.current).forEach(([key, sound]) => {
      if (!sound) return;
      const baseVolume = SOUNDS[key as SoundKey]?.volume ?? 1;
      sound.volume(baseVolume * volume);
    });
  }, [volume]);

  useEffect(() => {
    const storedVolume = sfxVolumeStorage.get();
    if (storedVolume) {
      const parsed = clampVolume(parseFloat(storedVolume));
      setVolume(parsed);
    }

    refLoaded.current = true;

    (async () => {
      const howlSounds = await Promise.all(Object.entries(SOUNDS).map(async ([key, sound]) => {
        const src = await resolveSrc(sound.src);
        return {
          [key]: new Howl({ ...sound, src, volume: (sound.volume ?? 1) * refVolume.current }),
        }
      }));

      const sounds = howlSounds.reduce((acc, curr) => ({ ...acc, ...curr }), {}) as Partial<Record<SoundKey, Howl>>;
      setSounds(sounds);
      refSounds.current = sounds;

      Object.entries(sounds).forEach(([key, sound]) => {
        if (!sound) return;
        const baseVolume = SOUNDS[key as SoundKey]?.volume ?? 1;
        sound.volume(baseVolume * refVolume.current);
      });
    })();

    return () => {
      Object.values(refSounds.current).forEach((sound) => {
        sound.stop();
      });
      setSounds({});
      refSounds.current = {} as Partial<Record<SoundKey, Howl>>;
    };
  }, []);

  useEffect(() => {
    if (!isMobile) return;

    const handler = () => {
      for (const sound of Object.values(refSounds.current)) {
        sound.mute(document.hidden);
      }
    };

    document.addEventListener('visibilitychange', handler);
    return () => {
      document.removeEventListener('visibilitychange', handler);
    };
  }, [isMobile]);

  return <></>
}
